import * as React from "react";
import { Card, CardHeader, CardFooter, Text, Link, Toolbar, ToolbarButton, Badge } from "@fluentui/react-components";
import { Edit20Regular, Delete20Regular, Note20Regular } from "@fluentui/react-icons";
import UserAvatar from "../../users/components/UserAvatar";
import timeAgo from "../../../shared/utils/timeAgo";
import ItemDeleteDialog from "../../../shared/components/ItemDeleteDialog";
import TicketNoteEditor from "./TicketNoteEditor";
import Files from "../../files/components/Files";
import { useAuth } from "../../../provider/authProvider";

const TicketNoteCard = ({
  ticketUpdate,
  //currentUser,
  t,
  styles,
  handleTranslate,
  translateMutation,
  showTranslation,
  translatedContent,
  selectedLanguage,
  setSelectedLanguage,
  useDeleteTicketUpdate,
}) => {
  const currentUser = useAuth().user;
  const canBeModified = ticketUpdate.author_id === currentUser.id;
  const attachmentsBarRef = React.useRef(null);

  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  return (
    <Card
      id="ticket-note-card"
      className={styles.card}
      style={{ display: "flex", flexDirection: "column", minHeight: "auto", overflow: "visible" }}
    >
      <CardHeader
        className={styles.header}
        header={
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Note20Regular />
                <Text weight="semibold">{t("ticket.userAddedNote", { name: ticketUpdate.author })}</Text>
              </div>
              {handleTranslate && (
                <Link onClick={handleTranslate} style={{ fontSize: "12px" }}>
                  {translateMutation.isPending
                    ? t("nounPhrases.translating", { defaultValue: "Translating..." })
                    : showTranslation
                      ? t("nounPhrases.showOriginal", { defaultValue: "Show original" })
                      : t("nounPhrases.showTranslation", { defaultValue: "Translate" })}
                </Link>
              )}
            </div>
            <span style={{ fontSize: "12px", color: "#666", display: "flex", alignItems: "center" }}>
              {timeAgo(ticketUpdate.created_at)}
            </span>
          </div>
        }
        image={
          <UserAvatar
            user={{
              id: ticketUpdate.author_id,
              name: ticketUpdate.author,
              public_photo_id: ticketUpdate.public_author_photo_id,
            }}
          />
        }
        action={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Badge
              className={styles.badgeNote}
              shape="circular"
              appearance="filled"
              style={{
                backgroundColor: "#6D6D6D",
                color: "#FFFFFF",
                width: "fit-content",
                margin: "0",
                padding: "0 6px",
              }}
              aria-label={t("ticket.note", { defaultValue: "Note" }).toLocaleLowerCase()}
            >
              {t("ticket.note", { defaultValue: "Note" }).toLocaleLowerCase()}
            </Badge>
            <Toolbar size="small">
              <ToolbarButton
                appearance="subtle"
                disabled={!canBeModified}
                icon={<Edit20Regular />}
                onClick={() => setIsEditModalOpen(true)}
              />
              <ToolbarButton
                appearance="subtle"
                icon={<Delete20Regular />}
                disabled={!canBeModified}
                onClick={() => setIsDeleteDialogOpen(true)}
              />
            </Toolbar>
          </div>
        }
      />

      <div className={styles.body} style={{ minHeight: "auto", overflow: "visible", flex: 1 }}>
        <div
          style={{
            fontFamily: "inherit",
            fontSize: "14px",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            minHeight: "auto",
            overflow: "visible",
            width: "100%",
          }}
          dangerouslySetInnerHTML={{
            __html: showTranslation && translatedContent ? translatedContent : ticketUpdate.content,
          }}
        />
      </div>

      <CardFooter
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
          padding: "4px",
        }}
      >
        <Files entity_type="ticket_update" entity_id={ticketUpdate.id} ref={attachmentsBarRef} />
      </CardFooter>

      <TicketNoteEditor
        ticketUpdate={ticketUpdate}
        isOpen={isEditModalOpen}
        onOpenChange={(e, data) => setIsEditModalOpen(data.open)}
        value={ticketUpdate.content}
        selectedLanguage={selectedLanguage}
        onLanguageChange={(lang) => setSelectedLanguage(lang)}
      />

      <ItemDeleteDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        useMutationHook={useDeleteTicketUpdate}
        id={ticketUpdate.id}
        name={t("generic.note", { defaultValue: "note" }).toLocaleLowerCase()}
        queryKey={["TicketUpdates", ticketUpdate.ticket_id]}
      />
    </Card>
  );
};

export default TicketNoteCard;
